<template>
  <NuxtLayout>
    <div class="min-h-screen bg-white text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="() => {}"
        @menu-click="() => {}"
        @login="onHeaderLogin"
        @how-it-works-click="() => {}"
      />

      <section class="w-full px-4 pb-6 pt-8 sm:px-6">
        <div class="mx-auto w-full max-w-[960px]">
          <NuxtLink
            :to="backPath"
            class="inline-flex items-center gap-2 text-sm font-semibold text-purple-950/70 hover:text-purple-950"
          >
            <span aria-hidden="true">←</span>
            <span>{{ t("newsletter.subscribe.back") }}</span>
          </NuxtLink>
        </div>
      </section>

      <section class="w-full px-4 pb-12 sm:px-6">
        <div
          class="mx-auto grid w-full max-w-[960px] gap-6 lg:grid-cols-[1.2fr_0.8fr]"
        >
          <div
            class="overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900 via-purple-800 to-pink-700 p-8 text-white shadow-xl"
          >
            <p
              class="text-xs font-semibold uppercase tracking-[0.24em] text-white/70"
            >
              {{ t("newsletter.subscribe.eyebrow") }}
            </p>
            <h1 class="mt-3 text-3xl font-bold sm:text-4xl">
              {{ establishmentName }}
            </h1>
            <p class="mt-4 max-w-xl text-sm leading-6 text-white/85">
              {{
                t("newsletter.subscribe.subtitle", {
                  establishment: establishmentName,
                })
              }}
            </p>
            <div
              class="mt-8 rounded-2xl border border-white/15 bg-white/10 p-4 text-sm text-white/85 backdrop-blur-sm"
            >
              {{ t("newsletter.subscribe.note") }}
            </div>
          </div>

          <div
            class="rounded-3xl border border-purple-100 bg-white p-6 shadow-sm sm:p-8"
          >
            <div v-if="pending" class="flex justify-center py-12">
              <div
                class="h-10 w-10 animate-spin rounded-full border-4 border-purple-100 border-t-pink-500"
              />
            </div>

            <div v-else class="space-y-5">
              <div>
                <h2 class="text-xl font-bold text-purple-950">
                  {{ t("newsletter.subscribe.formTitle") }}
                </h2>
                <p class="mt-2 text-sm text-purple-950/60">
                  {{ t("newsletter.subscribe.formBody") }}
                </p>
              </div>

              <div
                v-if="estError"
                class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {{ t("newsletter.subscribe.loadError") }}
              </div>

              <div
                v-if="successMessage"
                class="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"
              >
                {{ successMessage }}
              </div>

              <div
                v-if="errorMessage"
                class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {{ errorMessage }}
              </div>

              <form class="space-y-4" @submit.prevent="submit">
                <div>
                  <label
                    for="newsletter-email"
                    class="mb-2 block text-sm font-semibold text-purple-950"
                  >
                    {{ t("newsletter.subscribe.emailLabel") }}
                  </label>
                  <input
                    id="newsletter-email"
                    v-model="email"
                    type="email"
                    required
                    :placeholder="t('newsletter.subscribe.emailPlaceholder')"
                    class="w-full rounded-2xl border-2 border-purple-100 px-4 py-3 text-sm text-purple-950 outline-none transition focus:border-pink-500"
                  />
                </div>

                <button
                  type="submit"
                  :disabled="submitting || !establishmentId"
                  class="inline-flex w-full items-center justify-center rounded-2xl bg-pink-500 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-pink-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span
                    v-if="submitting"
                    class="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                  />
                  {{ t("newsletter.subscribe.submit") }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import AppHeader from "~/components/header/AppHeader.vue";
import { ApiClient, type ApiClientOptions } from "~~/shared/api/ApiClient";
import { useApiBaseUrl } from "~~/shared/models";
import { EstablishmentService } from "~~/shared/services/establishment";
import { extractUuidFromSlug } from "~~/shared/utils/slugUtils";

type Mode = "event" | "establishment";

const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { APP_URL } = usePublicUrls();

const mode = ref<Mode>("establishment");
const onModeChange = (next: Mode) => {
  mode.value = next;
};
const onHeaderLogin = () => {
  window.location.href = `${APP_URL}/`;
};

const opts: ApiClientOptions = { baseUrl: useApiBaseUrl() };
const client = new ApiClient(opts);
const establishmentService = new EstablishmentService(client);

const establishmentSlug = route.params.establishmentSlug as string;
const resolvedEstablishmentId =
  extractUuidFromSlug(establishmentSlug) ?? establishmentSlug;

const {
  data: establishment,
  pending,
  error: estError,
} = useAsyncData(`est-newsletter-${establishmentSlug}`, () =>
  establishmentService.fetchEstablishment(resolvedEstablishmentId),
);

const establishmentId = computed(
  () => establishment.value?.id ?? resolvedEstablishmentId,
);
const establishmentName = computed(
  () => establishment.value?.name ?? t("newsletter.subscribe.defaultName"),
);
const backPath = computed(() =>
  localePath(`/establishment/${establishmentSlug}`),
);

const email = ref("");
const submitting = ref(false);
const successMessage = ref<string | null>(null);
const errorMessage = ref<string | null>(null);

async function submit() {
  if (!establishmentId.value) return;
  submitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;
  try {
    await $fetch("/bff/newsletter-subscribe", {
      method: "POST",
      body: {
        email: email.value,
        establishmentId: establishmentId.value,
        sourceName: establishmentName.value,
      },
    });
    successMessage.value = t("newsletter.subscribe.success", {
      establishment: establishmentName.value,
    });
    email.value = "";
  } catch {
    errorMessage.value = t("newsletter.subscribe.error");
  } finally {
    submitting.value = false;
  }
}

useSeoMeta({
  title: () => `${establishmentName.value} Newsletter | Upfest`,
  description: () =>
    t("newsletter.subscribe.subtitle", {
      establishment: establishmentName.value,
    }),
});
</script>
